import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

/**
 * Module that updates the `--lerp` CSS variable on elements
 * based on current scroll velocity.
 */
export class StringLerp extends StringModule {
  private hasInitializedCSS = false;
  protected defaultModeScope = ["smooth"];
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lerp";

    this.cssProperties = [
      { name: "--lerp", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.attributesToMap = [];
  }

  /**
   * Initialize lerp value when object connects.
   */
  onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    object.setProperty<number>("lerp-value", 0);
    object.setProperty<number>("lerp-applied", 0);
    object.setProperty<string>("event-lerp-name", this.getObjectEventName(object, "object:lerp"));
  }

  /**
   * Called on resize - use this to apply initial values after mirrors are created.
   */
  onResize(): void {
    super.onResize();
    // Only apply initial CSS on first resize (after mirrors are created)
    if (!this.hasInitializedCSS && this.objectsOnPage.length > 0) {
      this.hasInitializedCSS = true;
      styleTxn.run(() => {
        for (let i = 0; i < this.objectsOnPage.length; i++) {
          this.updateObjectLerp(this.objectsOnPage[i], true);
        }
      });
    }
  }

  /**
   * Resets the `--lerp` value to 0 when scroll stops.
   */
  onScrollStop(): void {
    for (let i = 0; i < this.objects.length; i++) {
      this.recomputeLerp(this.objects[i], 0);
    }
  }

  /**
   * Updates `--lerp` value for each connected object during scroll.
   */
  onFrame(data: StringData): void {
    const velocity = data.scroll.lerped;
    for (let i = 0; i < this.objects.length; i++) {
      this.recomputeLerp(this.objects[i], velocity);
    }
  }

  /**
   * Computes the lerp value for the object.
   */
  private recomputeLerp(object: StringObject, velocity: number): void {
    const prevLerp = object.getProperty<number>("lerp-value") ?? 0;

    if (prevLerp === velocity) {
      return;
    }

    object.setProperty<number>("lerp-value", velocity);
    this.updateObjectLerp(object);
  }

  /**
   * Applies the lerp value to the object and its mirrors.
   */
  onMutate(): void {
    styleTxn.run(() => {
      const len = this.objects.length;
      for (let i = 0; i < len; i++) {
        this.updateObjectLerp(this.objects[i]);
      }
    });
  }

  /**
   * Updates the CSS variable on the object and connected elements.
   */
  private updateObjectLerp(object: StringObject, force = false): void {
    const lerp = object.getProperty<number>("lerp-value") ?? 0;
    const prevLerp = object.getProperty<number>("lerp-applied");

    if (!force && prevLerp === lerp) {
      return;
    }

    object.setProperty<number>("lerp-applied", lerp);

    const lerpStr = lerp?.toString() ?? "0";

    const eventName = object.getProperty<string>("event-lerp-name");
    if (eventName) {
      this.events.emit(eventName, lerp);
    }

    styleTxn.run(() => {
      this.applyVarToElement(object, "--lerp", lerpStr);
      this.applyVarToConnects(object, "--lerp", lerpStr);
    });
  }

  /**
   * Cleans up the CSS variable when object is disconnected.
   */
  onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);

    const clearCustomProperty = (el: HTMLElement) => {
      el.style.removeProperty("--lerp");
    };

    clearCustomProperty(object.htmlElement);
    const mirrors = object.mirrorObjects;
    for (let i = 0; i < mirrors.length; i++) {
      clearCustomProperty(mirrors[i].htmlElement);
    }
  }
}
